import { useEffect, useRef, useState } from 'react';
import { StreamHandler } from "../apis/streaming";

export class StreamSource<T> {
    url: string;

    constructor(url : string) {
        this.url = url;
    }
}

type VersionOptions = {
    idAttribute: string
    versionAttribute: string,
    xref?: Map<string, any>
}

export type StreamOptions = {
    versionOptions?: VersionOptions
}

export function Stream<T>(source?: StreamSource<T>, options?: StreamOptions): [T | undefined, boolean, any] {
    const [data, setData] = useState<T | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);
    const currentUrlRef = useRef<string | null>(null);
    const versionOptions = options?.versionOptions;

    useEffect(() => {
        if(!source) {
            setData(undefined);
            setLoading(false);
            setError(undefined);
            currentUrlRef.current = null;
            return;
        }

        if(currentUrlRef.current !== source.url) {
            setData(undefined);
            setLoading(false);
            setError(undefined);
        }
        currentUrlRef.current = source.url;

        if (versionOptions && versionOptions.xref) {
            const xref = versionOptions.xref;
            const _unsubscribe = StreamHandler.get().subscribe(source.url, (_data: unknown) => {
                const newData = (_data as any[]).map(current => {
                    const cached = xref.get(current[versionOptions.idAttribute]);
                    if (cached && (cached[versionOptions.versionAttribute] > current[versionOptions.versionAttribute])) {
                        return cached;
                    }

                    xref.set(current[versionOptions.idAttribute], { ...current });
                    return current;
                });

                setData(<T>newData);
                setLoading(false);
            });

            return () => {
                _unsubscribe();
            };
        } else {
            return StreamHandler.get().subscribe(source.url, (_data: unknown) => {
                setData(<T>_data);
                setLoading(false);
            });
        }
    }, [source?.url, versionOptions?.idAttribute, versionOptions?.versionAttribute, versionOptions?.xref]);

    return [data, loading, error];
}
